// ============================================================================
// JARVISTRAVEL - API FAILURE REPORTING (JAR-450)
// The one way an api module reports a failure it is handling. See AGENTS.md in
// this directory for the failure contract these modules follow.
// ============================================================================

import { captureMessage, type CaptureContext } from '@/app/utils/observability';

/**
 * The shape of `ApiRequestError` we read, matched structurally rather than by
 * importing the class: reporting must not depend on the transport module (it
 * would force every `./client` mock to re-export the class), and a duck-typed
 * read works just as well for an error that crossed a module boundary.
 */
interface ApiErrorLike {
  code?: string;
  detailCode?: string;
  message?: string;
  status?: number;
  isNetworkError?: boolean;
  rawBody?: string;
}

function asApiError(error: unknown): ApiErrorLike | undefined {
  if (!error || typeof error !== 'object') return undefined;
  const candidate = error as ApiErrorLike;
  // Either signal is enough. Requiring `code` meant a wrapper that preserved
  // only `isNetworkError` — BankSyncError does exactly that when the transport
  // failed before any response existed to carry a code — was not recognised as
  // an API error at all (review M1).
  if (typeof candidate.code === 'string') return candidate;
  if (typeof candidate.isNetworkError === 'boolean') return candidate;
  return undefined;
}

// Fallback notices fire every poll cycle; unchecked they bury real errors
// (JAR-292). Dedupe per scope+code so a repeat is quiet but a *new* kind of
// failure for the same call still reports.
const reported = new Set<string>();
const REPORTED_MAX = 200;

/**
 * Report an API failure that the module is handling (degrading from), so it
 * reaches the observability seam instead of dying in a bare `console.warn`.
 *
 * Preserves the classification `client.ts` already computed — `code`,
 * `detailCode`, and `isNetworkError` — which the bare console calls discarded.
 * That distinction is the point: a transport failure (offline, DNS, CORS) and a
 * real backend answer are different problems, and only the former is worth a
 * retry.
 *
 * This reports; it does not decide. Whether the caller degrades or propagates is
 * the contract in AGENTS.md, not this function's business.
 */
export function reportApiFailure(scope: string, error?: unknown, context?: CaptureContext): void {
  const api = asApiError(error);
  const code = api?.code ?? (error instanceof Error ? error.name : error ? 'unknown' : 'none');
  // No text from the network reaches the seam (JAR-1824). A backend refusal can
  // carry its driver's own words, and those can hold what a person typed: a
  // duplicate key names the value. The seam will feed a real sink, so an API
  // error is reported by its code, detail code and status. A local Error's
  // message is this app's own text, and stays.
  const message = !api && error instanceof Error ? error.message : undefined;

  const key = `${scope}:${code}`;
  if (reported.has(key)) return;
  if (reported.size >= REPORTED_MAX) reported.clear();
  reported.add(key);

  captureMessage(`[api] ${scope}`, 'warning', {
    code,
    ...(api?.detailCode ? { detailCode: api.detailCode } : {}),
    // The distinction client.ts computed and every bare console.warn discarded:
    // a transport failure is retryable, a backend answer is not.
    network: api?.isNetworkError ?? code === 'NetworkError',
    ...(api?.status ? { status: api.status } : {}),
    ...(message ? { message } : {}),
    // The excerpt of an unparseable body. Set only for ParseError, and the only
    // thing that says WHICH proxy or error page answered — without it a
    // ParseError is indistinguishable from any other (review M2). Core's own
    // refusals are JSON and never land here, so this is a proxy's page, not a
    // message that could quote a person's input.
    ...(api?.rawBody ? { rawBody: api.rawBody } : {}),
    ...context,
  });
}

/** Test hook — clears the once-per-session dedup memory. */
export function resetApiFailureReports(): void {
  reported.clear();
}

/**
 * Turn an API failure into something a person can read.
 *
 * The audit (JAR-355, A3/A4) found raw transport and parse strings reaching
 * toasts unnormalised: handlers wrote `e instanceof Error ? e.message : fallback`,
 * and `.message` could be a CDN's HTML error page, a DNS failure string, or a
 * backend stack trace. Two live sites did exactly this.
 *
 * The rule is about *provenance*, not severity:
 *
 * - a message the backend authored in a JSON error body was written for a user,
 *   so it is shown (bounded, in case it wasn't)
 * - a transport failure has no message anyone wrote — it gets a connectivity one
 * - an unparseable body is not a message at all; the caller's fallback wins
 *
 * `isNetworkError` is the classification `client.ts` already computes and, until
 * this function, nothing consumed.
 */
export function userMessage(error: unknown, fallback: string): string {
  const api = asApiError(error);
  if (!api) return error instanceof Error && error.message ? clamp(error.message, fallback) : fallback;

  if (api.isNetworkError) {
    return 'Couldn’t reach JarvisTravel. Check your connection and try again.';
  }
  // ParseError's message is now a fixed sentence, but a body could still arrive
  // as the message from an older caller or a hand-built error.
  if (api.code === 'ParseError' || api.code === 'UnknownError') return fallback;

  return clamp(api.message, fallback);
}

/**
 * A user-facing string has to be short enough to be a sentence. Anything longer
 * is a document that leaked into a toast, so fall back rather than truncate it
 * into something half-readable.
 */
const USER_MESSAGE_MAX = 200;

function clamp(message: string | undefined, fallback: string): string {
  if (!message) return fallback;
  const trimmed = message.trim();
  if (!trimmed || trimmed.length > USER_MESSAGE_MAX) return fallback;
  // A message carrying markup is a page, not a sentence.
  //
  // Deliberately over-broad: this also rejects "Use <b> for emphasis" and a
  // bare <support@x.com>. That direction is the safe one — the cost is a
  // generic fallback on a message that would have been fine, against the
  // benefit of never rendering a proxy's error page as a toast. Narrow it only
  // with a real example of a backend message it wrongly swallows (review L4).
  if (/<\/?[a-z][\s\S]*>/i.test(trimmed)) return fallback;
  return trimmed;
}

/**
 * HTTP statuses that mean "answered, but try again", not a verdict: a request
 * timeout (408) and a rate limit (429). One definition for the api modules that
 * classify a transient failure (JAR-2419), so trips.ts's trip-read verdict and
 * privacy.ts's retry decision read the same rule instead of each re-listing it.
 */
export function isTransientStatus(status: number): boolean {
  return status === 408 || status === 429;
}
